import {
    parseISO,
    addMonths,
    startOfMonth,
    format,
    isBefore,
    isValid,
} from "date-fns";
import { fullMonthFullYearFormat } from "@common/helpers/date";

const MAX_MONTH_ITERATIONS = 120;

export interface MonthSeed {
    key: string;
    label: string;
}

function collectMonthKeys(
    start: Date,
    end: Date,
    maxIterations: number = MAX_MONTH_ITERATIONS,
): string[] {
    const endMonth = startOfMonth(end);
    const keys: string[] = [];
    let current = startOfMonth(start);
    while (
        (isBefore(current, endMonth) ||
            current.getTime() === endMonth.getTime()) &&
        keys.length < maxIterations
    ) {
        keys.push(format(current, fullMonthFullYearFormat));
        current = addMonths(current, 1);
    }
    return keys;
}

export function generateMonthlySeeds(
    periodStart: string,
    periodEnd: string,
): MonthSeed[] {
    if (!periodStart || !periodEnd) {
        return [];
    }

    const start = parseISO(periodStart);
    const end = parseISO(periodEnd);
    if (!isValid(start) || !isValid(end)) {
        return [];
    }

    return collectMonthKeys(start, end).map((key) => ({ key, label: key }));
}

export function getMonthKeyFromDate(dateStr: string): string | null {
    try {
        const parsed = parseISO(dateStr);
        if (!isValid(parsed)) {
            return null;
        }
        return format(parsed, fullMonthFullYearFormat);
    } catch {
        return null;
    }
}

export function getMonthKeysForRange(
    startDateStr: string,
    endDateStr: string,
): string[] {
    const start = parseISO(startDateStr);
    const end = parseISO(endDateStr);
    if (!isValid(start) || !isValid(end) || isBefore(end, start)) {
        return [];
    }
    return collectMonthKeys(start, end);
}

interface DocWithTaxonomyDates {
    taxonomyExtraction?: {
        periodStartDate?: string | null;
        periodEndDate?: string | null;
    } | null;
}

export function getCoveredMonthKeys(doc: DocWithTaxonomyDates): string[] {
    const endDate = doc.taxonomyExtraction?.periodEndDate;
    if (!endDate) {
        return [];
    }
    const startDate = doc.taxonomyExtraction?.periodStartDate;
    if (!startDate) {
        const key = getMonthKeyFromDate(endDate);
        return key ? [key] : [];
    }
    const keys = getMonthKeysForRange(startDate, endDate);
    if (keys.length === 0) {
        const key = getMonthKeyFromDate(endDate);
        return key ? [key] : [];
    }
    // When a statement starts mid-month (e.g. credit card: Jan 15 → Feb 14),
    // the first calendar month is a partial carry-over from the billing cycle,
    // not a period the statement covers. Drop it — only full months count.
    if (keys.length > 1) {
        const start = parseISO(startDate);
        if (isValid(start) && start.getDate() !== 1) {
            return keys.slice(1);
        }
    }
    return keys;
}
